import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiArrowLeft, FiRefreshCw, FiCheck, FiBriefcase, FiTarget, FiDollarSign, FiTrendingUp, FiAward, FiClock, FiUsers, FiCalendar, FiGlobe, FiUserPlus, FiInstagram, FiMail, FiMonitor, FiStar, FiEye, FiHeart } = FiIcons;

const variants = {
  enter: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
};

const SingleQuestionFlow = ({ calcType, inputs, updateInput, onRestart, onComplete }) => {
  const [current, setCurrent] = useState(0);
  const [dir, setDir] = useState(1);
  const [rawValue, setRawValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const getQuestions = () => {
    const isAssociation = inputs.organizationType === 'association';
    
    if (calcType === 'website') {
      const base = [
        {
          key: 'organizationType',
          label: "What type of organization do you represent?",
          type: 'select',
          options: [
            { label: 'Nonprofit / Charity', value: 'nonprofit' },
            { label: 'Association', value: 'association' }
          ],
          hint: "This adjusts the calculator logic for members vs donors.",
          icon: FiGlobe
        },
        { key: 'annualOperatingBudget', label: "What is your annual operating budget?", hint: "Your total organizational budget", prefix: '$', icon: FiBriefcase },
        { key: 'websiteInvestment', label: "How much are you planning to invest in a new website?", hint: "Planned investment for redesign or rebuild", prefix: '$', icon: FiTarget }
      ];

      const membership = isAssociation ? [
        { key: 'memberCount', label: "How many members do you currently have?", hint: "Active paying members", icon: FiUsers },
        { key: 'membershipFee', label: "Average annual membership fee?", hint: "The primary source of member revenue", prefix: '$', icon: FiDollarSign },
        { key: 'memberGrowthGoal', label: "Target member growth % with a better website?", hint: "How many more members could you attract?", suffix: '%', icon: FiUserPlus },
        {
          key: 'acceptsDonations',
          label: "Do you also accept donations or grants?",
          type: 'select',
          options: [
            { label: 'No', value: 'no' },
            { label: 'Yes', value: 'yes' }
          ],
          hint: "Many associations also run foundations or accept grants.",
          icon: FiAward
        }
      ] : [];

      const donations = (!isAssociation || inputs.acceptsDonations === 'yes') ? [
        { key: 'currentDonations', label: "What are your current annual online donations?", hint: "Total donations received through your website", prefix: '$', icon: FiDollarSign },
        { key: 'donationGrowthGoal', label: "What donation growth % do you expect from a better website?", hint: "Industry average is 15–25%", suffix: '%', icon: FiTrendingUp }
      ] : [];

      const efficiency = [
        { key: 'staffHourlyRate', label: "What is your staff hourly cost (with benefits)?", hint: "Used to calculate time savings", prefix: '$', icon: FiDollarSign },
        { key: 'currentWebsiteHours', label: "Hours per week staff spend on website tasks?", hint: "Updates, maintenance, troubleshooting", suffix: ' hrs/wk', icon: FiClock },
        { key: 'eventAttendees', label: "What is your average event attendance?", hint: "Average attendees at fundraising events", icon: FiUsers }
      ];

      return [...base, ...membership, ...donations, ...efficiency];
    } else {
      const base = [
        {
          key: 'organizationType',
          label: "What type of organization do you represent?",
          type: 'select',
          options: [
            { label: 'Nonprofit / Charity', value: 'nonprofit' },
            { label: 'Association', value: 'association' }
          ],
          icon: FiGlobe
        },
        { key: 'annualOperatingBudget', label: "What is your annual operating budget?", hint: "Your total organizational budget", prefix: '$', icon: FiBriefcase },
        { key: 'brandingInvestment', label: "How much are you planning to invest in branding?", hint: "Planned investment for comprehensive branding", prefix: '$', icon: FiTarget }
      ];

      const membership = isAssociation ? [
        { key: 'memberCount', label: "Current total members?", icon: FiUsers },
        { key: 'membershipFee', label: "Average annual fee?", prefix: '$', icon: FiDollarSign },
        { key: 'memberGrowthGoal', label: "Target member growth % with stronger branding?", suffix: '%', icon: FiUserPlus }
      ] : [];

      const status = [
        { key: 'brandConsistencyRating', label: "Rate your brand consistency (1–10)", hint: "How consistent is your branding across channels?", suffix: '/10', max: 10, icon: FiStar },
        { key: 'visualAppealRating', label: "Rate your visual appeal (1–10)", hint: "How visually appealing is your current brand?", suffix: '/10', max: 10, icon: FiEye },
        { key: 'donorRetentionGoal', label: `Expected ${isAssociation ? 'Member' : 'Donor'} retention % increase?`, hint: "Stronger brands keep people longer", suffix: '%', icon: FiHeart },
        { key: 'socialMediaFollowers', label: "Total social media followers?", icon: FiInstagram },
        { key: 'emailListSize', label: "How many people are on your email list?", icon: FiMail },
        { key: 'monthlyWebsiteVisitors', label: "Monthly website visitors?", icon: FiMonitor }
      ];

      return [...base, ...membership, ...status];
    }
  };

  const questions = getQuestions();
  const q = questions[current] || questions[0];
  const isLast = current === questions.length - 1;
  const progress = ((current + 1) / questions.length) * 100;
  const accent = calcType === 'website' ? '#3b82f6' : '#a855f7';

  useEffect(() => {
    if (current >= questions.length) {
      setCurrent(questions.length - 1);
    }
  }, [questions.length, current]);

  const go = (delta) => {
    if (delta > 0 && isLast) {
      onComplete && onComplete();
      return;
    }
    setDir(delta);
    setRawValue('');
    setIsFocused(false);
    setCurrent(c => Math.max(0, Math.min(questions.length - 1, c + delta)));
  };

  const handleSelect = (val) => {
    updateInput(prev => ({ ...prev, [q.key]: val }));
    setTimeout(() => go(1), 300);
  };

  const handleChange = (e) => {
    const clean = e.target.value.replace(/[^\d.]/g, '').replace(/(\..*?)\..*/g, '$1');
    setRawValue(clean);
    const num = clean === '' ? 0 : parseFloat(clean);
    if (!isNaN(num)) {
      const capped = q.max ? Math.min(num, q.max) : num;
      updateInput(prev => ({ ...prev, [q.key]: capped }));
    }
  };

  // Hardened display logic to prevent "undefined"
  const val = inputs[q.key];
  const displayVal = isFocused 
    ? rawValue 
    : (val === undefined || val === null || val === 0 ? '' : Number(val).toLocaleString('en-US'));

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="h-1 bg-gray-100">
        <motion.div className="h-full" animate={{ width: `${progress}%` }} style={{ background: accent }} />
      </div>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <button onClick={onRestart} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
            <SafeIcon icon={FiRefreshCw} className="text-xs" /> Start over
          </button>
          <span className="text-xs font-medium text-gray-400 uppercase tracking-widest">
            {current + 1} / {questions.length}
          </span>
        </div>

        <AnimatePresence mode="wait" custom={dir}>
          <motion.div key={q.key} custom={dir} variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }}>
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-4" style={{ color: accent }}>
                <SafeIcon icon={q.icon} /> {calcType === 'website' ? 'Website ROI' : 'Branding ROI'}
              </div>
              <h2 className="text-2xl font-bold text-gray-900 leading-snug mb-2">{q.label}</h2>
              <p className="text-gray-400 text-sm">{q.hint}</p>
            </div>

            {q.type === 'select' ? (
              <div className="grid gap-3 mb-8">
                {q.options.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => handleSelect(opt.value)}
                    className={`w-full p-4 rounded-xl border-2 text-left font-bold transition-all ${inputs[q.key] === opt.value ? 'bg-gray-50 border-gray-900' : 'border-gray-100 hover:border-gray-200'}`}
                    style={{ borderColor: inputs[q.key] === opt.value ? accent : '' }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="relative mb-8">
                {q.prefix && <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg font-medium">{q.prefix}</span>}
                <input
                  type="text"
                  inputMode="decimal"
                  value={displayVal}
                  onChange={handleChange}
                  onFocus={() => { 
                    setIsFocused(true); 
                    const currentVal = inputs[q.key];
                    setRawValue(currentVal === undefined || currentVal === null || currentVal === 0 ? '' : String(currentVal)); 
                  }}
                  onBlur={() => setIsFocused(false)}
                  onKeyDown={(e) => e.key === 'Enter' && go(1)}
                  autoFocus
                  className={`w-full text-2xl font-bold text-gray-900 bg-gray-50 border-2 rounded-xl py-5 px-5 outline-none transition-all ${q.prefix ? 'pl-10' : ''} ${q.suffix ? 'pr-20' : ''}`}
                  style={{ borderColor: isFocused ? accent : '#e5e7eb' }}
                  placeholder="0"
                />
                {q.suffix && <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">{q.suffix}</span>}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between">
          <button onClick={() => go(-1)} disabled={current === 0} className="flex items-center gap-2 text-gray-400 hover:text-gray-700 disabled:opacity-0 font-medium">
            <SafeIcon icon={FiArrowLeft} /> Back
          </button>
          <button onClick={() => go(1)} className="flex items-center gap-2 font-bold px-6 py-3 rounded-xl text-white transition-all hover:opacity-90" style={{ background: accent }}>
            {isLast ? <><SafeIcon icon={FiCheck} /> See Results</> : <>Next <SafeIcon icon={FiArrowRight} /></>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SingleQuestionFlow;